import { RedirectToTasks, useAuth } from '@clerk/react-router';
import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import {
  rememberSessionTaskCompletionTarget,
  resolveSessionTaskCompletionTarget,
  SESSION_TASK_PATHS,
} from './sessionTaskHandoff';

const SESSION_TASK_ROUTE_PATHS = Object.values(SESSION_TASK_PATHS);

const isSessionTaskPath = (pathname: string): boolean =>
  SESSION_TASK_ROUTE_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

/**
 * Core 3 treats a session with unfinished tasks as signed out by default. Compare
 * that default view with Clerk's inclusive view so pending sessions are sent back
 * to their task UI instead of falling through the application's signed-out routes.
 */
export const PendingSessionBoundary = ({ children }: PropsWithChildren) => {
  const defaultAuth = useAuth();
  const inclusiveAuth = useAuth({ treatPendingAsSignedOut: false });
  const location = useLocation();
  const [preparedTarget, setPreparedTarget] = useState<string | null>(null);
  const isTaskRoute = isSessionTaskPath(location.pathname);
  const isPendingSession =
    defaultAuth.isLoaded &&
    inclusiveAuth.isLoaded &&
    defaultAuth.isSignedIn === false &&
    inclusiveAuth.isSignedIn === true;
  const completionTarget = useMemo(
    () =>
      // Auth pages record the current flow while signed out. Read that target
      // when the session becomes pending, even if the URL has not changed.
      isPendingSession
        ? resolveSessionTaskCompletionTarget(
            {
              pathname: location.pathname,
              search: location.search,
              hash: location.hash,
            },
            window.location.origin
          )
        : null,
    [isPendingSession, location.hash, location.pathname, location.search]
  );

  useEffect(() => {
    if (!completionTarget || isTaskRoute) {
      setPreparedTarget(null);
      return;
    }

    rememberSessionTaskCompletionTarget(completionTarget);
    setPreparedTarget(completionTarget);
  }, [completionTarget, isTaskRoute]);

  if (isPendingSession && !isTaskRoute) {
    // RedirectToTasks performs its navigation in an effect. Wait until a prior
    // render has persisted the destination so an immediate task completion cannot
    // race the storage write.
    return preparedTarget === completionTarget ? <RedirectToTasks /> : null;
  }

  return children;
};
